// src/features/auth/signup/pages/SignupPage.tsx

import React, {
  FormEvent,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import RouteConfig
  from '../../../../routes/RouteConfig';

import DuplicateCheckField
  from '../components/DuplicateCheckField';

import PhoneVerificationField
  from '../components/PhoneVerificationField';

import SignupField
  from '../components/SignupField';

import SignupSuccessModal
  from '../components/SignupSuccessModal';

import StoreSelect
  from '../components/StoreSelect';

import {
  useDuplicateCheck,
} from '../hooks/useDuplicateCheck';

import {
  useStores,
} from '../hooks/useStores';

import {
  usePhoneVerification,
} from '../../phone-verification/hooks/usePhoneVerification';

import {
  signupService,
} from '../services/signupService';

import type {
  DuplicateField,
  SignupFormData,
  SignupRequest,
} from '../types/signup.types';

import {
  getErrorMessage,
  normalizePhoneNumber,
  normalizeUserId,
  validateSignupForm,
} from '../utils/signup.utils';


const initialFormData:
  SignupFormData = {
  userId: '',
  password: '',
  userName: '',
  userNickname: '',
  storeId: 0,
  phoneNumber: '',
};


export default function SignupPage():
  React.JSX.Element {
  const navigate =
    useNavigate();


  const [
    formData,
    setFormData,
  ] =
    useState<SignupFormData>(
      initialFormData,
    );


  const [
    passwordConfirm,
    setPasswordConfirm,
  ] =
    useState('');


  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState('');


  const [
    isSubmitting,
    setIsSubmitting,
  ] =
    useState(false);


  const [
    signupSuccess,
    setSignupSuccess,
  ] =
    useState(false);


  /**
   * =========================================================
   * 매장 목록
   * =========================================================
   */
  const {
    storeList,
    isLoadingStores,
    storeErrorMessage,
  } =
    useStores();


  /**
   * =========================================================
   * 아이디 / 닉네임 중복 확인
   * =========================================================
   */
  const {
    duplicateStatus,
    duplicateMessages,
    checkDuplicate,
    resetDuplicateField,
  } =
    useDuplicateCheck();


  /**
   * =========================================================
   * 전화번호 인증
   *
   * 회원가입 전용 purpose 사용
   *
   * POST /api/v2/auth/phone/send
   *
   * {
   *   phoneNumber,
   *   purpose: "SIGNUP"
   * }
   *
   * POST /api/v2/auth/phone/verify
   *
   * {
   *   phoneNumber,
   *   verificationCode,
   *   purpose: "SIGNUP"
   * }
   * =========================================================
   */
  const phoneVerification =
    usePhoneVerification({
      phoneNumber:
        formData.phoneNumber,

      purpose:
        'SIGNUP',
    });


  /**
   * =========================================================
   * 공통 CSS
   * =========================================================
   */
  const inputClassName = `
    min-h-11 w-full rounded-lg
    border border-gray-300
    bg-white px-3.5 py-2.5
    text-sm text-gray-900
    outline-none transition

    placeholder:text-gray-400

    focus:border-main
    focus:ring-2
    focus:ring-main/20

    disabled:cursor-not-allowed
    disabled:bg-gray-100
    disabled:text-gray-500

    sm:min-h-12
    sm:rounded-xl
    sm:px-4
    sm:py-3
  `;


  const actionInputClassName = `
    min-h-11 min-w-0
    flex-1 rounded-lg
    border border-gray-300
    bg-white px-3 py-2.5
    text-sm text-gray-900
    outline-none transition

    placeholder:text-gray-400

    focus:border-main
    focus:ring-2
    focus:ring-main/20

    disabled:cursor-not-allowed
    disabled:bg-gray-100
    disabled:text-gray-500

    sm:min-h-12
    sm:rounded-xl
    sm:px-4
    sm:py-3
  `;


  const labelClassName =
    'mb-1.5 block text-sm font-semibold text-gray-700 sm:mb-2';


  /**
   * =========================================================
   * 비밀번호 일치 여부
   * =========================================================
   */
  const passwordMatches =
    passwordConfirm.length >
      0 &&
    formData.password ===
      passwordConfirm;


  const passwordMismatch =
    passwordConfirm.length >
      0 &&
    formData.password !==
      passwordConfirm;


  /**
   * =========================================================
   * Form 값 변경
   * =========================================================
   */
  const updateField = <
    K extends keyof SignupFormData,
  >(
    field: K,
    value: SignupFormData[K],
  ): void => {
    setFormData(
      (previous) => ({
        ...previous,

        [field]:
          value,
      }),
    );


    if (
      errorMessage
    ) {
      setErrorMessage('');
    }


    /**
     * 아이디 또는 닉네임을
     * 중복확인한 이후 다시 변경하면
     * 중복확인 상태를 초기화합니다.
     */
    if (
      field ===
        'userId' ||
      field ===
        'userNickname'
    ) {
      resetDuplicateField(
        field as DuplicateField,
      );
    }
  };


  /**
   * =========================================================
   * 전화번호 변경
   * =========================================================
   */
  const handlePhoneNumberChange = (
    phoneNumber: string,
  ): void => {
    /**
     * 전화번호가 변경되면
     * 기존 인증번호 / 인증 토큰을
     * 모두 무효화합니다.
     */
    phoneVerification
      .resetVerification();


    updateField(
      'phoneNumber',
      phoneNumber,
    );
  };


  /**
   * =========================================================
   * 회원가입
   * =========================================================
   */
  const handleSubmit =
    async (
      event:
        FormEvent<HTMLFormElement>,
    ): Promise<void> => {
      event.preventDefault();


      if (
        isSubmitting ||
        signupSuccess
      ) {
        return;
      }


      setErrorMessage('');


      /**
       * 전체 Form 검증
       */
      const validationMessage =
        validateSignupForm({
          formData,

          passwordConfirm,

          duplicateStatus,

          phoneVerified:
            phoneVerification
              .phoneVerified,

          verificationToken:
            phoneVerification
              .verificationToken,

          storeList,
        });


      if (
        validationMessage
      ) {
        setErrorMessage(
          validationMessage,
        );

        return;
      }


      /**
       * validateSignupForm에서
       * 이미 검사하지만
       * TypeScript / 런타임 안정성을 위해
       * 한 번 더 검사합니다.
       */
      const verificationToken =
        phoneVerification
          .verificationToken;


      if (
        !verificationToken
      ) {
        setErrorMessage(
          '휴대폰 인증을 다시 진행해주세요.',
        );

        return;
      }


      const requestBody:
        SignupRequest = {
        userId:
          formData
            .userId
            .trim(),

        password:
          formData.password,

        userName:
          formData
            .userName
            .trim(),

        userNickname:
          formData
            .userNickname
            .trim(),

        storeId:
          formData.storeId,

        phoneNumber:
          normalizePhoneNumber(
            formData.phoneNumber,
          ),

        /**
         * 현재 기존 API 필드 유지
         *
         * 추후 추천인 코드가 추가되면
         * 실제 입력값으로 변경합니다.
         */
        code: '',

        /**
         * SIGNUP 용도로 발급된
         * 전화번호 인증 토큰
         */
        verificationToken,
      };


      try {
        setIsSubmitting(
          true,
        );


        await signupService
          .signup(
            requestBody,
          );


        setSignupSuccess(
          true,
        );

      } catch (
        error: unknown
      ) {
        setErrorMessage(
          getErrorMessage(
            error,
            '회원가입 처리 중 오류가 발생했습니다.',
          ),
        );

      } finally {
        setIsSubmitting(
          false,
        );
      }
    };


  /**
   * =========================================================
   * 로그인 페이지 이동
   * =========================================================
   */
  const moveToLogin = (
    replace = false,
  ): void => {
    navigate(
      RouteConfig
        .Auth
        .Login
        .fullPath,
      {
        replace,
      },
    );
  };


  return (
    <main
      className="
        min-h-screen
        bg-white

        md:grid
        md:grid-cols-2
        md:bg-gray-100

        lg:grid-cols-[3fr_2fr]
      "
    >

      {/* =====================================================
          왼쪽 브랜드 영역
          ===================================================== */}
      <section
        className="
          relative
          hidden
          min-h-screen
          overflow-hidden
          bg-zinc-950

          md:flex
          md:items-center
          md:justify-center
        "
      >

        <div
          className="
            absolute
            inset-0

            bg-gradient-to-br
            from-black/10
            via-transparent
            to-emerald-950/30
          "
          aria-hidden="true"
        />


        <div
          className="
            relative
            z-10
            w-full
            max-w-2xl
            px-10
          "
        >

          <img
            src="/login.png"
            alt="LAPOIT"
            className="
              mx-auto
              h-auto
              w-full
              max-w-lg
              object-contain
            "
          />

        </div>

      </section>


      {/* =====================================================
          회원가입 영역
          ===================================================== */}
      <section
        className="
          flex
          justify-center

          px-4
          pb-10
          pt-8

          sm:px-8
          sm:pt-14

          md:min-h-screen
          md:items-center
          md:px-10
          md:py-10

          lg:px-14
        "
      >

        <div
          className="
            w-full
            max-w-md
          "
        >

          {/* 모바일 로고 */}
          <div
            className="
              mb-5
              md:hidden
            "
          >

            <span
              className="
                text-sm
                font-black
                tracking-[0.24em]
                text-main
              "
            >
              LAPOIT
            </span>

          </div>


          {/* =================================================
              Header
              ================================================= */}
          <header
            className="
              mb-6
              md:mb-8
            "
          >

            <span
              className="
                hidden
                text-xs
                font-bold
                tracking-[0.2em]
                text-main

                md:block
              "
            >
              LAPOIT MEMBER
            </span>


            <h1
              className="
                text-2xl
                font-bold
                tracking-tight
                text-gray-900

                sm:text-3xl

                md:mt-3
                md:text-4xl
              "
            >
              회원가입
            </h1>


            <p
              className="
                mt-2
                text-sm
                leading-6
                text-gray-500
              "
            >
              휴대폰 인증 후 바로
              회원가입할 수 있습니다.
            </p>

          </header>


          {/* =================================================
              Form
              ================================================= */}
          <form
            onSubmit={
              handleSubmit
            }
            className="
              space-y-4
              sm:space-y-5
            "
            noValidate
          >

            {/* =================================================
                아이디
                ================================================= */}
            <DuplicateCheckField
              id="userId"
              label="아이디"
              value={
                formData.userId
              }
              placeholder="영문 시작, 영문·숫자 4~20자"
              status={
                duplicateStatus
                  .userId
              }
              message={
                duplicateMessages
                  .userId
              }
              helperText="영문으로 시작하며 숫자를 1개 이상 포함해야 합니다."
              autoComplete="username"
              maxLength={20}
              disabled={
                isSubmitting
              }
              inputClassName={
                actionInputClassName
              }
              labelClassName={
                labelClassName
              }
              onChange={(
                value,
              ) => {
                updateField(
                  'userId',

                  normalizeUserId(
                    value,
                  ),
                );
              }}
              onCheck={() => {
                void checkDuplicate(
                  'userId',

                  formData.userId,
                );
              }}
            />


            {/* =================================================
                비밀번호
                ================================================= */}
            <div
              className="
                grid
                grid-cols-1
                gap-4

                lg:grid-cols-2
                lg:gap-5
              "
            >

              <SignupField
                id="password"
                label="비밀번호"
                type="password"
                value={
                  formData.password
                }
                placeholder="영문, 숫자 포함 8자 이상"
                autoComplete="new-password"
                disabled={
                  isSubmitting
                }
                inputClassName={
                  inputClassName
                }
                labelClassName={
                  labelClassName
                }
                onChange={(
                  value,
                ) =>
                  updateField(
                    'password',
                    value,
                  )
                }
              />


              <SignupField
                id="passwordConfirm"
                label="비밀번호 확인"
                type="password"
                value={
                  passwordConfirm
                }
                placeholder="비밀번호 다시 입력"
                autoComplete="new-password"
                disabled={
                  isSubmitting
                }
                inputClassName={`
                  ${inputClassName}

                  ${
                    passwordMismatch
                      ? 'border-red-400'
                      : ''
                  }

                  ${
                    passwordMatches
                      ? 'border-emerald-400'
                      : ''
                  }
                `}
                labelClassName={
                  labelClassName
                }
                message={
                  passwordMismatch
                    ? '비밀번호가 일치하지 않습니다.'
                    : passwordMatches
                      ? '비밀번호가 일치합니다.'
                      : undefined
                }
                messageType={
                  passwordMismatch
                    ? 'error'
                    : 'success'
                }
                onChange={
                  setPasswordConfirm
                }
              />

            </div>


            {/* =================================================
                이름
                ================================================= */}
            <SignupField
              id="userName"
              label="이름"
              value={
                formData.userName
              }
              placeholder="이름을 입력해주세요"
              maxLength={30}
              disabled={
                isSubmitting
              }
              inputClassName={
                inputClassName
              }
              labelClassName={
                labelClassName
              }
              onChange={(
                value,
              ) =>
                updateField(
                  'userName',
                  value,
                )
              }
            />


            {/* =================================================
                닉네임
                ================================================= */}
            <DuplicateCheckField
              id="userNickname"
              label="닉네임"
              value={
                formData
                  .userNickname
              }
              placeholder="닉네임 2~12자"
              status={
                duplicateStatus
                  .userNickname
              }
              message={
                duplicateMessages
                  .userNickname
              }
              maxLength={12}
              disabled={
                isSubmitting
              }
              inputClassName={
                actionInputClassName
              }
              labelClassName={
                labelClassName
              }
              onChange={(
                value,
              ) =>
                updateField(
                  'userNickname',
                  value,
                )
              }
              onCheck={() => {
                void checkDuplicate(
                  'userNickname',

                  formData
                    .userNickname,
                );
              }}
            />


            {/* =================================================
                전화번호 인증
                ================================================= */}
            <PhoneVerificationField
              phoneNumber={
                formData
                  .phoneNumber
              }
              verificationCode={
                phoneVerification
                  .verificationCode
              }
              verificationSent={
                phoneVerification
                  .verificationSent
              }
              phoneVerified={
                phoneVerification
                  .phoneVerified
              }
              isSendingVerification={
                phoneVerification
                  .isSendingVerification
              }
              isVerifyingCode={
                phoneVerification
                  .isVerifyingCode
              }
              resendSeconds={
                phoneVerification
                  .resendSeconds
              }
              verificationSeconds={
                phoneVerification
                  .verificationSeconds
              }
              message={
                phoneVerification
                  .message
              }
              disabled={
                isSubmitting
              }
              inputClassName={
                actionInputClassName
              }
              labelClassName={
                labelClassName
              }
              onPhoneNumberChange={
                handlePhoneNumberChange
              }
              onVerificationCodeChange={
                phoneVerification
                  .updateVerificationCode
              }
              onSendVerification={() => {
                void phoneVerification
                  .sendVerification();
              }}
              onVerifyCode={() => {
                void phoneVerification
                  .verifyCode();
              }}
            />


            {/* =================================================
                소속 매장
                ================================================= */}
            <StoreSelect
              storeId={
                formData.storeId
              }
              storeList={
                storeList
              }
              isLoading={
                isLoadingStores
              }
              errorMessage={
                storeErrorMessage
              }
              disabled={
                isSubmitting
              }
              inputClassName={
                inputClassName
              }
              labelClassName={
                labelClassName
              }
              onChange={(
                storeId,
              ) =>
                updateField(
                  'storeId',
                  storeId,
                )
              }
            />


            {/* =================================================
                전체 에러
                ================================================= */}
            {errorMessage && (

              <div
                className="
                  rounded-lg
                  bg-red-50
                  px-4
                  py-3
                  text-sm
                  text-red-600
                "
              >
                {errorMessage}
              </div>

            )}


            {/* =================================================
                회원가입 버튼
                ================================================= */}
            <button
              type="submit"
              disabled={
                isSubmitting ||
                !phoneVerification
                  .phoneVerified ||
                !phoneVerification
                  .verificationToken ||
                isLoadingStores ||
                storeList.length ===
                  0
              }
              className="
                flex
                min-h-11
                w-full
                items-center
                justify-center

                rounded-lg
                bg-main
                px-4
                py-2.5

                text-sm
                font-semibold
                text-white

                transition

                hover:bg-main-medium

                disabled:cursor-not-allowed
                disabled:opacity-60

                sm:min-h-12
                sm:text-base
              "
            >

              {isSubmitting
                ? '가입 처리 중'
                : '회원가입'}

            </button>

          </form>


          {/* =================================================
              로그인 이동
              ================================================= */}
          <div
            className="
              mt-5
              border-t
              border-gray-100
              pt-5
              text-center
            "
          >

            <button
              type="button"
              onClick={() =>
                moveToLogin()
              }
              className="
                text-sm
                text-gray-500

                hover:text-main
              "
            >

              이미 계정이 있으신가요?

              <span
                className="
                  ml-1
                  font-semibold
                  text-main
                "
              >
                로그인
              </span>

            </button>

          </div>

        </div>

      </section>


      {/* =====================================================
          회원가입 성공
          ===================================================== */}
      <SignupSuccessModal
        open={
          signupSuccess
        }
        onLogin={() =>
          moveToLogin(
            true,
          )
        }
      />

    </main>
  );
}